import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { RouteMetadata } from '../shared/models/route-metadata.model';

/**
 * NavigationContextService
 *
 * Provides reactive navigation context signals derived from the active route
 * tree's `data` metadata — not from URL string matching.
 *
 * Usage:
 *   private readonly navCtx = inject(NavigationContextService);
 *   readonly isChat = this.navCtx.isChatContext;      // Signal<boolean>
 *
 * Extending for new routes:
 *   1. Add `navContext: 'chat' | 'workspace' | 'workspace-settings' | 'workspace-recent-chats' | 'system'` to the route `data`
 *      in DynamicRouteService.
 *   2. All signals update automatically — no changes needed here.
 */
@Injectable({ providedIn: 'root' })
export class NavigationContextService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  /**
   * Current navigation context derived from the deepest active route's `data`.
   * Updates on every NavigationEnd event and is available immediately on load.
   */
  readonly navContext = computed(() => this.routeData()?.navContext ?? null);

  /** True when active route is a chat page (agent chat, legacy /chat/:agentName). */
  readonly isChatContext = computed(() => this.navContext() === 'chat');

  /**
   * True when active route is any workspace-scoped page (catalog OR settings).
   * Use this when you need a single boolean covering all workspace pages.
   * Use isWorkspaceSettingsContext for settings-specific logic.
   */
  readonly isWorkspaceContext = computed(
    () =>
      this.navContext() === 'workspace' ||
      this.navContext() === 'workspace-settings' ||
      this.navContext() === 'workspace-recent-chats',
  );

  /** True when active route is the workspace settings page (/workspaces/settings?subscription_id=:id). */
  readonly isWorkspaceSettingsContext = computed(
    () => this.navContext() === 'workspace-settings',
  );

  /** True when active route is a system page (workspace launcher, welcome). */
  readonly isSystemContext = computed(() => this.navContext() === 'system');

  /**
   * Raw route metadata signal — reactive because it's driven by router events.
   * Consumers get a fresh snapshot on every NavigationEnd without needing a
   * separate getDeepestData() call.
   */
  private readonly routeData = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.getDeepestData()),
      startWith(this.getDeepestData()),
    ),
    { initialValue: null as RouteMetadata | null },
  );

  /**
   * Walk the ActivatedRoute snapshot tree to the leaf route and return its
   * `data` object typed as RouteMetadata. Returns null when no data exists.
   */
  private getDeepestData(): RouteMetadata | null {
    let route = this.activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return (route.data as RouteMetadata) ?? null;
  }
}
